import { FC } from "react";
import ActionButton from "../../../../components/Buttons/ActionButton";

/**
 * Confirmation for deleting a quote line that has service lines and/or is a
 * Data Uploads line. Styled like QuoteDeleteModal1.
 */
const OrderProductDeleteModal: FC<{
  lineLabel: string;
  serviceLabels: string[];
  // The deleted line itself is a saved Data Uploads line.
  isDataUploadLine: boolean;
  // Some of its service lines are saved Data Uploads lines.
  hasDataUploadServices: boolean;
  onCancel: () => void;
  onDelete: (withServices: boolean) => void;
}> = ({
  lineLabel,
  serviceLabels,
  isDataUploadLine,
  hasDataUploadServices,
  onCancel,
  onDelete,
}) => {
  const serviceCount = serviceLabels.length;
  const servicesWord = serviceCount === 1 ? "service line" : "service lines";

  return (
    <div
      className="fixed inset-0 bg-transparent z-50 p-2 md:flex md:flex-row md:justify-center md:items-center"
      role="dialog"
      aria-modal="true"
      aria-label="Delete quote line"
    >
      <div className="fixed inset-0 bg-black/60 -z-20" onClick={onCancel} />
      <div className="w-[300px] sm:w-[560px] bg-[#e4e6e9] shadow-[0_0_64px_#000] mx-auto h-fit rounded-b-[6px]">
        <h3 className="bg-[#0078AE] max-w-[600px] text-[#000] font-semibold flex justify-between py-2 px-2">
          <span className="text-[16px] leading-5">Delete quote line</span>
          <span className="inline-flex justify-center items-center">
            <i
              onClick={onCancel}
              className="icon-cancel cursor-pointer text-white"
            />
          </span>
        </h3>
        <div className="text-sm p-6 flex flex-col gap-3">
          <p className="font-semibold text-center">
            Delete &quot;{lineLabel}&quot;?
          </p>
          {serviceCount > 0 && (
            <div>
              <p>
                This line has {serviceCount} {servicesWord}:
              </p>
              <ul className="list-disc pl-6">
                {serviceLabels.map((label, index) => (
                  <li key={`${label}-${index}`}>{label}</li>
                ))}
              </ul>
              <p className="mt-1">
                Delete them too, or keep them as separate lines?
              </p>
            </div>
          )}
          {(isDataUploadLine || hasDataUploadServices) && (
            <p
              className="text-orange-700"
              data-testid="order-product-delete-data-upload-warning"
            >
              {isDataUploadLine
                ? "This is a Data Uploads line: files the customer uploaded for it will lose their folder."
                : "Deleting the service lines also deletes a Data Uploads line: files the customer uploaded for it will lose their folder."}
            </p>
          )}
        </div>
        <div className="flex flex-wrap justify-center gap-4 p-3">
          <ActionButton
            buttonName="Cancel"
            className="hover:bg-[#888] cursor-pointer bg-[#A0A0A0] border-[#A0A0A0] whitespace-nowrap"
            buttonType="button"
            onClick={onCancel}
          />
          {serviceCount > 0 && (
            <ActionButton
              buttonName="Keep services"
              className="bg-[#6FB3E0] border-[#6FB3E0] hover:bg-[#4F99C6] whitespace-nowrap"
              buttonType="button"
              onClick={() => onDelete(false)}
            />
          )}
          <ActionButton
            buttonName={serviceCount > 0 ? "Delete with services" : "Delete"}
            buttonType="button"
            className="bg-[#D15B47] border-[#D15B47] hover:bg-[#B74635] whitespace-nowrap"
            onClick={() => onDelete(true)}
          />
        </div>
      </div>
    </div>
  );
};

export default OrderProductDeleteModal;
